import { z } from 'zod';
/** Snapshot key of module 40's own contributor for account tags (spec 40 §4.4b, S230-1). */
export const VARIANT_TAGS_SNAPSHOT_KEY = 'variant_tags';
/** Instagram collaborators per post: "up to 3" (Graph API reference `POST /<IG_ID>/media`, verified 2026-10-12, spec 70 §4.10). */
export const MAX_COLLABORATORS = 3;
/** Person tags per image: Instagram's app limit; the Graph reference names none (spec 70 §4.10). */
export const MAX_USER_TAGS_PER_IMAGE = 20;
/**
 * Instagram usernames (stored lower case, without `@`): letters, digits, periods and underscores, at most 30; a period
 * neither first, last nor twice in a row. Instagram publishes no official rule; this is what its sign-up field accepts
 * as reported by several handle checkers (checked 2026-10-13, spec 70 §4.10). Web and server both use it (S230-2).
 */
export const INSTAGRAM_USERNAME_PATTERN = /^(?=.{1,30}$)[a-z0-9_]+(?:\.[a-z0-9_]+)*$/;
/** The looser S230-1 rule, only for reading tags stored before S230-2 (Instagram refuses such a name at publishing). */
const STORED_USERNAME_PATTERN = /^[a-z0-9._]{1,30}$/;
/** `@Anna.Maier` and ` anna.maier ` both become `anna.maier`; whether the result is valid is `isInstagramUsername`'s question. */
export function normalizeInstagramUsername(raw) {
    return raw.trim().replace(/^@+/, '').toLowerCase();
}
export const isInstagramUsername = (value) => INSTAGRAM_USERNAME_PATTERN.test(value);
/** The public profile page of a (valid) username, for a manual look before publishing (S230-2, spec 70 §9 Q20 b). */
export const instagramProfileUrl = (username) => `https://www.instagram.com/${encodeURIComponent(username)}/`;
const usernameSchema = z.string().regex(STORED_USERNAME_PATTERN);
/** One person tag: `position` is the 0-based image in publish order, `x`/`y` the share of width/height from the top left. */
export const userTagSchema = z
    .object({
    position: z.number().int().min(0).max(9),
    username: usernameSchema,
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
})
    .strict();
/** The tags of one variant (spec 40 §4.4b). */
export const variantTagsSchema = z
    .object({
    variant_id: z.string().uuid(),
    collaborators: z.array(usernameSchema).max(MAX_COLLABORATORS),
    user_tags: z.array(userTagSchema),
})
    .strict();
/** The tags stored in a version snapshot (`contributions.variant_tags`), or none. */
export function snapshotVariantTags(snapshot) {
    const part = snapshot.contributions[VARIANT_TAGS_SNAPSHOT_KEY];
    return part === undefined ? [] : z.array(variantTagsSchema).parse(part);
}
/** The tags of a variant in a list, or `null`. */
export function variantTagsOf(tags, variantId) {
    return tags.find((entry) => entry.variant_id === variantId) ?? null;
}
/** What a format can carry (spec 70 §4.10): feed and carousel both, a reel collaborators only, stories and Facebook nothing. */
export function tagSupport(format) {
    if (format === 'ig_feed' || format === 'ig_carousel')
        return { collaborators: true, userTags: true };
    if (format === 'ig_reel')
        return { collaborators: true, userTags: false };
    return { collaborators: false, userTags: false };
}
/**
 * Follows the tags to a new media order: `previous` and `next` are the variant's asset IDs by position before and
 * after a media replace. A tag stays with its image; the tags of a removed image go.
 */
export function remapUserTags(tags, previous, next) {
    return tags.flatMap((tag) => {
        const asset = previous[tag.position];
        const position = asset === undefined ? -1 : next.indexOf(asset);
        return position < 0 ? [] : [{ ...tag, position }];
    });
}
//# sourceMappingURL=variant-tags.js.map